/**
 * AspectRatioLabel — affichage normalisé d'un ratio image (S2.5e).
 *
 * Source unique pour rendre un ratio width/height de façon lisible :
 *   1.0   → "1:1"
 *   0.667 → "2:3"   (portrait, snap noble)
 *   1.5   → "3:2"   (paysage, snap noble)
 *   0.707 → "1:√2"  (DIN A portrait)
 *   1.857 → "~1.86:1" (fallback décimal — hors whitelist)
 *   null  → "—"     (image pas encore uploadée)
 *
 * Réutilise `ratioString` (`utils/formatLabel.ts`) — même algorithme que
 * le libellé format catalogue (snap whitelist {1:1, 3:2, 4:3, 5:4, 7:5,
 * √2:1, 16:9, 16:10, 21:9} à ±2 %, fallback décimal sinon).
 *
 * Pourquoi ? Évite que la fiche œuvre affiche "0.667" cru (cf. capture
 * Cherry / 2026-05-08) — l'opératrice attend la même grammaire que les
 * formats catalogue. Un seul composant garantit cohérence WorksPage /
 * WorkDetailPage / MockupTemplatesPage / etc.
 *
 * Le composant rend un `<span>` plat — pas de wrapper structurel — pour
 * pouvoir l'embarquer dans un `<dd>`, un `<Badge>`, un `title=`…
 */
import { ratioString } from '@/utils/formatLabel'

interface AspectRatioLabelProps {
  /**
   * Ratio width/height (signed, donc orientation-aware). NULL/undefined
   * → fallback `placeholder` (défaut `—`).
   */
  value: number | string | null | undefined
  /**
   * Texte affiché quand `value` est NULL/undefined ou non-numérique.
   * Défaut "—" (em-dash) pour s'aligner sur le reste de la fiche
   * conservateur.
   */
  placeholder?: string
  className?: string
}

export default function AspectRatioLabel({
  value,
  placeholder = '—',
  className,
}: AspectRatioLabelProps) {
  const numeric = value === null || value === undefined ? NaN : Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return <span className={className}>{placeholder}</span>
  }
  // Convention `ratioString(w, h)` : on passe (ratio, 1) — la fonction
  // détermine orientation par `w >= h` et applique le snap noble.
  return <span className={className}>{ratioString(numeric, 1)}</span>
}
